import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import { adminPaths } from "admin/paths";
import { normalizeSlug } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";
import { businessCategories } from "data/admin/domain";
import { businessesRepo } from "data/repositories/businessesRepo";
import { provincesRepo } from "data/repositories/provincesRepo";

const CATEGORY_LABELS = {
  hotel: "Otel",
  resort: "Resort",
  pension: "Pansiyon",
  restaurant: "Restoran",
  souvenir: "Hediyelik",
  transport: "Ulaşım",
  agency: "Tur Ajansı",
  guide: "Rehber",
};

function emptyBusiness() {
  return {
    id: "",
    name: "",
    description: "",
    category: "hotel",
    provinceId: "",
    district: "",
    town: "",
    addressLine: "",
    phone: "",
    email: "",
    website: "",
    whatsapp: "",
    priceFromText: "",
    imageUrl: "",
    ownerUserId: "",
  };
}

function mapBusinessToForm(business) {
  return {
    id: business.id || "",
    name: business.name || "",
    description: business.description || "",
    category: business.category || "hotel",
    provinceId: business.location?.provinceId || "",
    district: business.location?.district || "",
    town: business.location?.town || "",
    addressLine: business.location?.addressLine || "",
    phone: business.contact?.phone || "",
    email: business.contact?.email || "",
    website: business.contact?.website || "",
    whatsapp: business.contact?.whatsapp || "",
    priceFromText: business.priceFromText || "",
    imageUrl: business.imageUrl || "",
    ownerUserId: business.ownerUserId || "",
  };
}

function validateBusinessForm(form, isNew, businessId) {
  const nextErrors = {};
  const normalizedId = form.id ? normalizeSlug(form.id) : normalizeSlug(`${form.provinceId}-${form.name}`);

  if (!normalizedId) nextErrors.id = "İşletme ID üretilemedi.";
  else if (isNew && businessesRepo.getById(normalizedId)) nextErrors.id = "Bu ID ile kayıt zaten var.";
  else if (!isNew && normalizedId !== businessId) nextErrors.id = "Mevcut kayıtta ID değiştirilemez.";

  if (!form.name.trim()) nextErrors.name = "İşletme adı zorunludur.";
  if (!form.category) nextErrors.category = "Kategori zorunludur.";
  if (!form.provinceId) nextErrors.provinceId = "İl seçimi zorunludur.";
  if (!form.description.trim()) nextErrors.description = "Açıklama zorunludur.";
  if (!form.addressLine.trim()) nextErrors.addressLine = "Adres zorunludur.";
  if (!form.ownerUserId.trim()) nextErrors.ownerUserId = "Owner user ID zorunludur.";

  return { nextErrors, normalizedId };
}

export default function AdminBusinessEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;
  const [form, setForm] = useState(emptyBusiness());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const provinces = useMemo(() => provincesRepo.list().filter((province) => !province.deletedAt), []);

  useEffect(() => {
    setError("");
    if (isNew) {
      setForm(emptyBusiness());
      setErrors({});
      return;
    }

    const business = businessesRepo.getById(id);
    if (!business) {
      setError("İşletme bulunamadı");
      return;
    }

    setForm(mapBusinessToForm(business));
    setErrors({});
  }, [id, isNew]);

  const onChange = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      [field]: field === "id" ? normalizeSlug(value) : value,
    }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setError("");

    const { nextErrors, normalizedId } = validateBusinessForm(form, isNew, id);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {
      id: normalizedId,
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      location: {
        provinceId: form.provinceId,
        district: form.district.trim(),
        town: form.town.trim(),
        addressLine: form.addressLine.trim(),
      },
      contact: {
        phone: form.phone.trim(),
        email: form.email.trim(),
        website: form.website.trim(),
        whatsapp: form.whatsapp.trim(),
      },
      priceFromText: form.priceFromText.trim(),
      imageUrl: form.imageUrl.trim(),
      ownerUserId: form.ownerUserId.trim(),
    };

    try {
      setSaving(true);
      if (isNew) businessesRepo.create(payload);
      else businessesRepo.update(id, payload);

      navigate(`/admin${adminPaths.admin.businesses}`, {
        state: {
          status: {
            color: "success",
            message: `${payload.name} başarıyla ${isNew ? "oluşturuldu" : "güncellendi"}.`,
          },
        },
      });
    } catch (submitError) {
      setError(submitError?.message || "Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const currentBusiness = isNew ? null : businessesRepo.getById(id);

  return (
    <>
      <AdminPageHeader
        title={isNew ? "Yeni işletme" : "İşletme düzenle"}
        description="Konum ve iletişim alanları mobile business modeliyle birebir hizalanır."
        backAction={() => navigate(`/admin${adminPaths.admin.businesses}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Temel bilgiler</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}

                <Form onSubmit={onSubmit}>
                  <FormGroup>
                    <Label>ID</Label>
                    <Input
                      value={form.id}
                      onChange={onChange("id")}
                      disabled={!isNew}
                      placeholder="Boş bırakırsanız il ve isimden türetilir"
                      invalid={Boolean(errors.id)}
                    />
                    <FormFeedback>{errors.id}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>İşletme adı</Label>
                    <Input value={form.name} onChange={onChange("name")} invalid={Boolean(errors.name)} />
                    <FormFeedback>{errors.name}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Kategori</Label>
                        <Input type="select" value={form.category} onChange={onChange("category")} invalid={Boolean(errors.category)}>
                          {businessCategories.map((category) => (
                            <option key={category} value={category}>
                              {CATEGORY_LABELS[category] || category}
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{errors.category}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>İl</Label>
                        <Input type="select" value={form.provinceId} onChange={onChange("provinceId")} invalid={Boolean(errors.provinceId)}>
                          <option value="">Seçin</option>
                          {provinces.map((province) => (
                            <option key={province.id} value={province.id}>
                              {province.name} ({province.id})
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{errors.provinceId}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Açıklama</Label>
                    <Input type="textarea" rows="5" value={form.description} onChange={onChange("description")} invalid={Boolean(errors.description)} />
                    <FormFeedback>{errors.description}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>İlçe</Label>
                        <Input value={form.district} onChange={onChange("district")} />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Belde / Semt</Label>
                        <Input value={form.town} onChange={onChange("town")} />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Owner User ID</Label>
                        <Input value={form.ownerUserId} onChange={onChange("ownerUserId")} invalid={Boolean(errors.ownerUserId)} placeholder="u-business-trabzon" />
                        <FormFeedback>{errors.ownerUserId}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Adres</Label>
                    <Input value={form.addressLine} onChange={onChange("addressLine")} invalid={Boolean(errors.addressLine)} />
                    <FormFeedback>{errors.addressLine}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Telefon</Label>
                        <Input value={form.phone} onChange={onChange("phone")} />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>E-posta</Label>
                        <Input value={form.email} onChange={onChange("email")} type="email" />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Website</Label>
                        <Input value={form.website} onChange={onChange("website")} placeholder="https://..." />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>WhatsApp</Label>
                        <Input value={form.whatsapp} onChange={onChange("whatsapp")} placeholder="+90..." />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Fiyat metni</Label>
                        <Input value={form.priceFromText} onChange={onChange("priceFromText")} placeholder="₺1.250 / gece" />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Görsel URL</Label>
                        <Input value={form.imageUrl} onChange={onChange("imageUrl")} placeholder="https://..." />
                      </FormGroup>
                    </Col>
                  </Row>

                  <AdminFormActions saving={saving} onCancel={() => navigate(`/admin${adminPaths.admin.businesses}`)} />
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Durum</h3>
              </CardHeader>
              <CardBody>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Bu ekran admin tarafında işletme lifecycle yönetimi içindir; public detail akışı mevcut business detail sayfasını kullanır.
                </div>
                {!isNew ? (
                  <div className="mt-3">
                    <Label className="d-block">Kayıt durumu</Label>
                    <Badge color={currentBusiness?.deletedAt ? "warning" : "success"} pill>
                      {currentBusiness?.deletedAt ? "Arşivde" : "Aktif"}
                    </Badge>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}